import React, { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  CircularProgress,
  TextField,
  IconButton,
  Snackbar,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { api } from '../../../redux/api';
import VersionConflictDialog from './VersionConflictDialog';

function toFiniteInt(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function normalizeDeployErrors(errors) {
  if (errors == null) {
    return [];
  }
  if (Array.isArray(errors)) {
    return errors.filter((e) => e != null && String(e).trim() !== '');
  }
  return [String(errors)];
}

function appendBatchStripNotes(baseMessage, stripNotes) {
  if (!stripNotes || !stripNotes.length) {
    return baseMessage;
  }
  return `${baseMessage}\n\n${stripNotes.join('\n')}`;
}

/** Normalizes workbench JSON (summary may use snake_case; counts may be strings). */
function unpackDeployBatchResponse(responseBody) {
  if (!responseBody || typeof responseBody !== 'object') {
    return {
      current_state: null,
      summary: {},
      blockedFiles: [],
      errors: [],
      deployedCount: 0,
      failedCount: 0,
    };
  }
  const inner =
    responseBody.data != null && typeof responseBody.data === 'object'
      ? responseBody.data
      : responseBody;
  const summary =
    inner.summary != null && typeof inner.summary === 'object' ? inner.summary : {};
  const deployedCount = toFiniteInt(
    summary.deployedCount ?? summary.deployed_count ?? inner.deployedCount,
  );
  const failedCount = toFiniteInt(
    summary.failedCount ?? summary.failed_count ?? inner.failedCount,
  );
  const blockedFiles = Array.isArray(inner.blockedFiles) ? inner.blockedFiles : [];
  const errors = normalizeDeployErrors(inner.errors);
  const results = Array.isArray(inner.results) ? inner.results : [];
  const stripNotes = results
    .filter((r) => r && r.status === 'success' && r.message && String(r.message).trim())
    .map((r) => {
      const label = r.name ? `${r.name}: ` : '';
      return `${label}${r.message}`.trim();
    });
  return {
    current_state: responseBody.current_state,
    summary: { ...summary, deployedCount, failedCount },
    blockedFiles,
    errors,
    deployedCount,
    failedCount,
    stripNotes,
  };
}

/** Count deployable templates (leaf files), not folder rows — matches server deployedCount semantics. */
function getDeployableTemplateCount(nodes) {
  if (!nodes?.length) {
    return 0;
  }
  let total = 0;
  for (const node of nodes) {
    if (node.type === 'workflow_group') {
      const children = node.children || [];
      for (const ch of children) {
        if (ch.type === 'template_folder') {
          if (ch.children?.length) {
            total += ch.children.filter((c) => c.isTemplateFile || c.isDocument).length;
          } else {
            total += Number(ch.fileCount) || 0;
          }
        }
      }
    } else if (node.type === 'template_folder') {
      if (node.children?.length) {
        total += node.children.filter((c) => c.isTemplateFile || c.isDocument).length;
      } else {
        total += Number(node.fileCount) || 0;
      }
    } else if (node.isTemplateFile || node.isDocument) {
      total += 1;
    } else if (
      node.type &&
      ['INBOUND', 'OUTBOUND', 'WORKFLOW', 'SCHEDULE'].includes(node.type) &&
      node.title
    ) {
      total += 1;
    }
  }
  return total;
}

function DeployTemplatesToServer({ open, onClose, selectedNodes, onRefreshTree, urlParams, userSpaceChecked, serverChecked }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isDeploying, setIsDeploying] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [comment, setComment] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('');
  const [showVersionWarning, setShowVersionWarning] = useState(false);
  const [currentConflictItem, setCurrentConflictItem] = useState(null);
  const [conflictItems, setConflictItems] = useState([]);
  const [currentConflictIndex, setCurrentConflictIndex] = useState(0);
  const [conflictDecisions, setConflictDecisions] = useState([]);
  const [itemsToSkip, setItemsToSkip] = useState([]);
  const [blockedFiles, setBlockedFiles] = useState([]);
  const [originalComment, setOriginalComment] = useState('');
  const [firstPhaseDeployedCount, setFirstPhaseDeployedCount] = useState(0);
  const [firstPhaseErrors, setFirstPhaseErrors] = useState([]);
  /** Stable revision group id for entire deploy (phase 1 + 2), same as SWT single transfer */
  const batchGroupIdRef = useRef(null);

  useEffect(() => {
    if (!open) {
      batchGroupIdRef.current = null;
    }
  }, [open]);

  const folderTypeMap = {
    WORKFLOW: 'workflows',
    INBOUND: 'inbound_templates',
    OUTBOUND: 'outbound_templates',
    SCHEDULE: 'schedules',
  };

  const deployableTemplateCount = getDeployableTemplateCount(selectedNodes);

  const formatErrorList = (errors = []) => {
    const normalizedErrors = errors
      .filter(Boolean)
      .flatMap((error) =>
        String(error)
          .split(',')
          .map((part) => part.trim())
          .filter(Boolean)
      );
    return normalizedErrors
      .map((error, index) => `${index + 1}. ${error}`)
      .join('\n');
  };

 

  const handleVersionConflictResponse = (decision) => {
    // Record the decision for current conflict item
    const newDecisions = [...conflictDecisions];
    newDecisions[currentConflictIndex] = {
      item: conflictItems[currentConflictIndex],
      decision: decision
    };
    setConflictDecisions(newDecisions);
    
    if (decision === 'YES') {
      // Deploy current item, move to next conflict
      const nextIndex = currentConflictIndex + 1;
      if (nextIndex < conflictItems.length) {
        setCurrentConflictIndex(nextIndex);
        setCurrentConflictItem(conflictItems[nextIndex]);
        
      } else {
        setShowVersionWarning(false);
        proceedWithSecondPhaseDeployment(newDecisions);
      }
    } 
    else if (decision === 'YES_TO_ALL') {
      // Deploy all conflicting items at once
      setShowVersionWarning(false);
      proceedWithSecondPhaseDeployment(newDecisions);
    } 
    else if (decision === 'NO') {
      // Skip current item, add to skip list, move to next conflict
      const newItemsToSkip = [...itemsToSkip, conflictItems[currentConflictIndex]];
      setItemsToSkip(newItemsToSkip);
      
      const nextIndex = currentConflictIndex + 1;
      if (nextIndex < conflictItems.length) {
        setCurrentConflictIndex(nextIndex);
        setCurrentConflictItem(conflictItems[nextIndex]);
      } else {
        
        setShowVersionWarning(false);
        proceedWithSecondPhaseDeployment(newDecisions, newItemsToSkip);
      }
    } 
    else if (decision === 'NO_TO_ALL') {
      // Cancel deployment for all items
      setShowVersionWarning(false);
      setSnackbar({
        open: true,
        message: t('MSG_DEPLOY_BATCH_CANCELLED'),
        severity: 'info'
      });
      onClose();
    }
   
  };

  const handleDeploy = async () => {
    if (!comment.trim()) {
      setAlertMessage(t('LBL_NEW_COMMENT_REQUIRED') || 'Comment is required');
      setAlertType('error');
      return;
    }

    // Reset states for new deployment
    setConflictItems([]);
    setCurrentConflictIndex(0);
    setCurrentConflictItem(null);
    setConflictDecisions([]);
    setItemsToSkip([]);
    setBlockedFiles([]);
    
    setOriginalComment(comment.trim());

    // Start first phase deployment - API will check for conflicts
    proceedWithDeploy();
  };

  const proceedWithDeploy = async () => {
    setIsDeploying(true);
    setAlertMessage('');
    
    try {
      const nodesToDeploy = selectedNodes; 
      
      // Check if any items remain to deploy
      if (nodesToDeploy.length === 0) {
        setSnackbar({
          open: true,
          message: t('MSG_DEPLOY_BATCH_NONE'),
          severity: 'info'
        });
        onClose();
        return;
      }

      if (!batchGroupIdRef.current) {
        batchGroupIdRef.current = String(Date.now());
      }

      const selectedItems = nodesToDeploy.map(selectedNode => {
        let name = selectedNode.title;
        let path = '';
        let type = '';

        // Determine type and path based on node type
        if (selectedNode.type === 'workflow_group') {
          type = 'workflow_group';
          path = selectedNode.title;
          name = selectedNode.title;
        } else if (selectedNode.type === 'template_folder') {
          type = 'template_folder';
          path = selectedNode?.title === 'workflows' ? selectedNode.workflowGroup : `${selectedNode.workflowGroup}/${selectedNode.title}`;
          name = selectedNode.title;
        } else {
          // For template files
          type = 'file';
          path = `${selectedNode?.workflowGroup}${selectedNode?.type === 'WORKFLOW' ? '' : `/${folderTypeMap[selectedNode?.type]}`}/${selectedNode?.title}`
          name = selectedNode.title;
        }

        return {
          name: name,
          path: path,
          type: type
        };
      });

      // Prepare the complete API payload
      const apiPayload = {
        selectedItems: selectedItems,
        userspace: userSpaceChecked || false,
        server: serverChecked || false,
        account: urlParams.account,
        user: urlParams.user,
        comment: comment.trim(),
        batchGroupId: batchGroupIdRef.current,
      };

      const response = await dispatch(api.endpoints.deployTemplatesToServer.initiate({
        account: urlParams.account,
        data: apiPayload
      }));
      
      // Handle API response
      if (response.data) {
        const unpacked = unpackDeployBatchResponse(response.data);
        const { current_state, summary, blockedFiles, errors: errorMessages, stripNotes } = unpacked;
        const deployedCount = unpacked.deployedCount;
        const failedCount = unpacked.failedCount;

        // Check if there are blocked files that require user decision 
        if (blockedFiles && blockedFiles.length > 0) {
          setFirstPhaseDeployedCount(toFiniteInt(summary?.deployedCount));
          setFirstPhaseErrors(errorMessages);

          setBlockedFiles(blockedFiles);
          setConflictItems(blockedFiles);
          setCurrentConflictIndex(0);
          setCurrentConflictItem(blockedFiles[0]);
          setShowVersionWarning(true);
          setIsDeploying(false);
          return;
        }
        
        if (current_state === "SUCCESS") {
          // No blocked files, show deployment results
          if (deployedCount <= 0 && failedCount <= 0 && errorMessages.length > 0) {
            setSnackbar({
              open: true,
              message: t('MSG_DEPLOY_BATCH_FILES_FAILED', { errors: formatErrorList(errorMessages) }),
              severity: 'error'
            });
          } else if (deployedCount <= 0 && failedCount <= 0 && errorMessages.length === 0) {
            setSnackbar({
              open: true,
              message: t('MSG_DEPLOY_BATCH_ZERO_TEMPLATES_DEPLOYED'),
              severity: 'warning'
            });
          } else if (deployedCount > 0 && failedCount <= 0 && errorMessages.length === 0) {
            const notes = stripNotes || [];
            setSnackbar({
              open: true,
              message: appendBatchStripNotes(t('MSG_DEPLOY_BATCH_FILES_SUCCESS', { deployed: deployedCount }), notes),
              severity: notes.length ? 'warning' : 'success'
            });
          } else {
            if (deployedCount > 0) {
              const failedPart = failedCount > 0 ? t('MSG_DEPLOY_BATCH_FILES_ERROR_PREFIX', { failed: failedCount }) : '';
              const errorPart = errorMessages.length ? `\n${formatErrorList(errorMessages)}` : '';
              const notes = stripNotes || [];
              const partialBase = t('MSG_DEPLOY_BATCH_FILES_PARTIAL', { deployed: deployedCount, failedPart, errorPart });
              setSnackbar({
                open: true,
                message: appendBatchStripNotes(partialBase, notes),
                severity: 'warning'
              });
            } else if (deployedCount <= 0 && failedCount <= 0 && errorMessages.length === 0) {
              setSnackbar({
                open: true,
                message: t('MSG_DEPLOY_BATCH_ZERO_TEMPLATES_DEPLOYED'),
                severity: 'warning'
              });
            } else {
              setSnackbar({
                open: true,
                message: t('MSG_DEPLOY_BATCH_FILES_FAILED', { errors: formatErrorList(errorMessages) }),
                severity: 'error'
              });
            }
          }
        } else {
          const errList = errorMessages.length ? errorMessages : ['Unknown error occurred'];
          setSnackbar({
            open: true,
            message: `Failed to deploy items:\n${formatErrorList(errList)}`,
            severity: 'error'
          });
        }
        
        // Refresh the tree after API call
        if (onRefreshTree) {
          onRefreshTree();
        }
      } else if (response.error) {
        // Handle API error
        setSnackbar({
          open: true,
          message: `Error deploying items: ${response.error.message || 'Unknown error'}`,
          severity: 'error'
        });
      }
      
      // Close the dialog
      onClose();
      
    } catch (error) {
      console.error("Error deploying items:", error);
      setSnackbar({
        open: true,
        message: `Error deploying items: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setIsDeploying(false);
      setComment('')
    }
  };

  const proceedWithSecondPhaseDeployment = async (passedDecisions = null, passedItemsToSkip = null) => {
    setIsDeploying(true);
    
    try {
      const decisionsToUse = passedDecisions || conflictDecisions;
      const itemsToSkipToUse = passedItemsToSkip || itemsToSkip;
      
      // Prepare items for second phase deployment based on user decisions
      const selectedItems = [];
      
      // Check if "YES_TO_ALL" was selected - if so, include all non-skipped blocked files
      const hasYesToAll = decisionsToUse.some(d => d.decision === 'YES_TO_ALL');
      
      for (let i = 0; i < blockedFiles.length; i++) {
        const blockedFile = blockedFiles[i];
        
        // Check if this item should be skipped
        const isSkipped = itemsToSkipToUse.some(skipItem => 
          skipItem.path === blockedFile.path && 
          skipItem.name === blockedFile.name
        );
        
        // Include item if not skipped AND either "YES_TO_ALL" was selected OR specific "YES" decision exists
        let shouldInclude = false;
        
        if (!isSkipped) {
          if (hasYesToAll) {
            shouldInclude = true;
          } else {
            // Find the decision for this specific index 
            const decision = decisionsToUse[i];
            shouldInclude = decision?.decision === 'YES';
          }
        }
        
        if (shouldInclude) {
          selectedItems.push({
            name: blockedFile.name,
            path: blockedFile.path,
            type: 'file',
            templateRevisionId: blockedFile.templateRevisionId,
            serverRevisionId: blockedFile.serverRevisionId
          });
        }
      }
      
      // If no items to deploy in second phase, just show results and close
      if (selectedItems.length === 0) {
        setSnackbar({
          open: true,
          message: t('MSG_DEPLOY_BATCH_NO_FILES_PHASE2'),
          severity: 'info'
        });
        onClose();
        return;
      }
      
      const apiPayload = {
        selectedItems: selectedItems,
        userspace: userSpaceChecked || false,
        server: serverChecked || false,
        account: urlParams.account,
        user: urlParams.user,
        comment: originalComment,
        overwrite: true,
        batchGroupId: batchGroupIdRef.current || String(Date.now()),
      };

      const response = await dispatch(api.endpoints.deployTemplatesToServer.initiate({
        account: urlParams.account,
        data: apiPayload
      }));
      
      if (response.data) {
        const unpacked = unpackDeployBatchResponse(response.data);
        const { current_state } = unpacked;
        const secondPhaseDeployedCount = unpacked.deployedCount;
        const failedCount = unpacked.failedCount;
        const totalDeployedCount = toFiniteInt(firstPhaseDeployedCount) + secondPhaseDeployedCount;

        if (current_state === "SUCCESS") {
          const secondPhaseErrors = unpacked.errors;
          const phase2StripNotes = unpacked.stripNotes || [];
          const allErrors = [...normalizeDeployErrors(firstPhaseErrors), ...secondPhaseErrors];

          if (failedCount <= 0 && allErrors.length === 0) {
            if (totalDeployedCount <= 0) {
              setSnackbar({
                open: true,
                message: t('MSG_DEPLOY_BATCH_ZERO_TEMPLATES_DEPLOYED'),
                severity: 'warning'
              });
            } else {
              setSnackbar({
                open: true,
                message: appendBatchStripNotes(
                  t('MSG_DEPLOY_BATCH_FILES_SUCCESS', { deployed: totalDeployedCount }),
                  phase2StripNotes
                ),
                severity: phase2StripNotes.length ? 'warning' : 'success'
              });
            }
          } else {
            const failedPart = failedCount > 0 ? t('MSG_DEPLOY_BATCH_FILES_ERROR_PREFIX', { failed: failedCount }) : '';
            const errorPart = allErrors.length ? `\n${formatErrorList(allErrors)}` : '';
            setSnackbar({
              open: true,
              message: t('MSG_DEPLOY_BATCH_FILES_PARTIAL', { deployed: totalDeployedCount, failedPart, errorPart }),
              severity: 'warning'
            });
          }
        } else {
          const errorMessages = unpacked.errors.length ? unpacked.errors : ['Unknown error occurred'];
          setSnackbar({
            open: true,
            message: `Failed to deploy items:\n${formatErrorList(errorMessages)}`,
            severity: 'error'
          });
        }
        
        // Refresh the tree after API call
        if (onRefreshTree) {
          onRefreshTree();
        }
      } else if (response.error) {
        setSnackbar({
          open: true,
          message: `Error deploying items: ${response.error.message || 'Unknown error'}`,
          severity: 'error'
        });
      }
      
      // Close the dialog
      onClose();
      
    } catch (error) {
      console.error("Error in second phase deployment:", error);
      setSnackbar({
        open: true,
        message: `Error deploying items: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setIsDeploying(false);
    }
  };

  const handleCancel = () => {
    batchGroupIdRef.current = null;
    setComment('');
    setAlertMessage('');
    setAlertType('');
    setConflictItems([]);
    setCurrentConflictIndex(0);
    setCurrentConflictItem(null);
    setConflictDecisions([]);
    setItemsToSkip([]);
    setBlockedFiles([]);
    setOriginalComment('');
    setFirstPhaseDeployedCount(0);
    setFirstPhaseErrors([]);
    setShowVersionWarning(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={!isDeploying ? onClose : undefined} hidden={showVersionWarning} maxWidth="sm" fullWidth>
        <DialogTitle className="dialog-header">
          <div className="flex space-between align-center">
            <div className="title">
              {t('DEPLOY_TO_SERVER') || 'Deploy to Server'}
            </div>
            <IconButton
              onClick={handleCancel}
              disabled={isDeploying}
              sx={{ borderRadius: "5px" }}
            >
              <CloseIcon
                sx={{
                  height: "28px",
                  width: "28px",
                  color: "#777777",
                }}
              />
            </IconButton>
          </div>
        </DialogTitle>
        
        <DialogContent className="dialog-content" dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>{t('WARNING') || 'Warning'}:</strong>{' '}
            {t('MSG_DEPLOY_BATCH_DEPLOY_CONFIRM', { deployable: deployableTemplateCount })}
          </Typography>
          <TextField
            label={t('LBL_NEW_COMMENT') || 'Comment'}
            multiline
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            fullWidth
            margin="normal"
            placeholder={t('LBL_ENTER_DEPLOYMENT_COMMENT') || 'Enter deployment comment'}
          />

          {alertMessage && (
            <p className={`${alertType === 'error' ? 'error' : 'success-message'}`}>
              {alertMessage}
            </p>
          )}
        </DialogContent>
        
        <DialogActions className="dialog-actions">
          <Button 
            variant="outlined" 
            onClick={handleCancel} 
            disabled={isDeploying}
          >
            {t("CANCEL") || "Cancel"}
          </Button>
          <Button 
            onClick={handleDeploy} 
            disabled={isDeploying || !comment.trim()}
            variant="outlined"
            color="primary"
            startIcon={isDeploying ? <CircularProgress size={16} /> : null}
          >
            {isDeploying ? (
              `${t('DEPLOYING') || 'Deploying'}...`
            ) : (
              t('DEPLOY_TO_SERVER') || 'Deploy to Server'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Version Conflict Warning Dialog */}
      <VersionConflictDialog
        open={showVersionWarning}
        onClose={() => setShowVersionWarning(false)}
        conflictItem={currentConflictItem}
        currentIndex={currentConflictIndex}
        totalConflicts={conflictItems.length}
        onResponse={handleVersionConflictResponse}
      />
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={['error', 'warning', 'info'].includes(snackbar.severity) ? null : 6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: '100%',
            maxWidth: '450px',
            alignItems: 'flex-start',
            '& .MuiAlert-message': {
              whiteSpace: 'pre-line',
              wordBreak: 'break-word'
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default DeployTemplatesToServer;